RivalIQ.apps.Dashboard.module('mixins', function (mixins, App, Backbone, Marionette, $, _, s) {

    'use strict';

    mixins.Export = {

        getExportParameters: function (format) {
            const me = this;
            return me.serializeObject(me.getPropsForFormat(format));
        },

        _baseExportProps: function () {
            const me = this;

            // NOTE: if we were to include scheduleId or triggerExport, then
            // exports triggered would themselves trigger more exports...
            // That would be A Bad Thing (tm)
            // NOTE: this list is duplicated in exportConfigurationHooks.js, if you change it here, change it there too
            const ignoreList = ['triggerExport', 'scheduleId', 'isSchedule'];

            return _.extend({
                isFocus: me.isFocus()
            }, _.omit(me.getStatelessProps(), ignoreList));
        },

        getPropsForFormat: function (format) {
            const me = this;

            const props = me._baseExportProps();
            props.format = format;
            props.orientation = me.getExportOrientation(format);
            props.exportWidthClass = me.getExportWidthClass();

            const formatProps = me[`${format}ExportProps`];
            if (formatProps) {
                return (_.isFunction(formatProps) ? formatProps.call(me, props) : _.extend({}, props, formatProps));
            }

            return me.exportProps(props);
        },

        // For supporting multiple pdf export widths
        // TODO remove after custom charts beta
        getExportWidth: _.constant(800),
        getUsePdfPostMargin: _.constant(false),
        getExportWidthClass: function () {
            const me = this;
            return (me.isExportHD() ? 'export-hd' : undefined);
        },

        getExportOrientation: _.constant(RivalIQ.enums.orientation.LANDSCAPE),

        getPropsForSchedule: function (format) {
            const me = this;
            let props = _.omit(me.getPropsForFormat(format), ['format'].concat(me.stateProps));

            // SEE #13313
            // When scheduling a metric CSV, we don't actually want the serialized metrics yet,
            // we only need enough to load the UI and kick off the actual CSV export.
            // Since this is exactly what the PNG export props do, we're going to just use those.
            // We currently do this for metric, facebook ads, and discovery csv export.
            if (format === RivalIQ.enums.exportFormat.CSV && _.has(props, 'metric') &&
                (_.includes(['facebookAdsInsightsGenerator', 'metric', 'discoveryDataGenerator'], _.get(props, ['type'])))) {
                props = _.omit(me.getPropsForFormat(RivalIQ.enums.exportFormat.PNG), ['format'].concat(me.stateProps));
            }

            return props;
        },

        // Props without state keys. Useful for generating routes, exports, etc
        getStatelessProps: function () {
            const me = this;
            return _.omit(me.getProps(), me.stateProps);
        },

        defaultExportProps: ['format', 'isFocus', 'orientation'],

        exportProps: function (props) {
            const me = this;

            if (me.propsToSerializeForExport) {
                props = _.pick(props, me.propsToSerializeForExport.concat(me.defaultExportProps));
            }

            return props;
        },

        // TODO replace w/ toJSON
        serializeObject: function (obj) {
            return _.reduce(obj, function (memo, v, k) {
                if (v) {
                    memo[k] = (v.serialize ? v.serialize() : v);
                } else {
                    memo[k] = v;
                }
                return memo;
            }, {});
        },

        getExportFormats: function () {
            const me = this;
            return RivalIQ.export.sortFormats(_.result(me, 'exportFormats'));
        },

        getExportTypes: function () {
            const me = this;
            return me.exportTypes;
        },

        getReportAttributes: function (format) {
            const me = this;

            const timePeriod = me.getProp('timePeriod');
            const meaningfulTimePeriod = !me.isTimePeriodDisabled();

            return {
                filename: me.getFilename(format),
                title: me.getTitle(format),
                timePeriodName: (meaningfulTimePeriod ? timePeriod.displayString : null),
                focusCompanyId: me.getProp('focusCompanyId'),
                activeSources: me.getProp('activeSources'),
                hideFromDownloads: false
            };
        },

        /*
         * Returns a url obj for export rendering
         */
        getExportPathObject: function (format, overrides) {
            const me = this;
            format = format || me.getProp('format');

            const props = me.getExportParameters(format, overrides);
            if (me.curation) {
                // TODO this approach will override widget props with values of
                // undefined, rather than only props that don't exist at all
                // (e.g., {foo: undefined}, and {}); this is something of a
                // problem for widgets, because a curation can stomp on your
                // deliberate undefined value (see #15553)
                _.defaults(props, _.omit(me.curation.getExportParameters(format, overrides), 'wProps'));
            }

            return me.getBaseUrlObject(props);
        },

        isExporting: function () {
            const me = this;
            return Boolean(me.getProp('format'));
        },

        isExportingPPT: function () {
            const me = this;
            return me.getProp('format') === RivalIQ.enums.exportFormat.PPT;
        },

        isExportingPDF: function () {
            const me = this;
            return me.getProp('format') === RivalIQ.enums.exportFormat.PDF;
        },

        isExportingPNG: function () {
            const me = this;
            return me.getProp('format') === RivalIQ.enums.exportFormat.PNG;
        },

        isScheduling: function () {
            const me = this;
            return Boolean(me.getProp('isSchedule'));
        },

        isPortrait: function () {
            const me = this;
            return me.getProp('orientation') === RivalIQ.enums.orientation.PORTRAIT;
        },

        isLandscape: function () {
            const me = this;
            return me.getProp('orientation') !== RivalIQ.enums.orientation.PORTRAIT;
        },

        buildScheduledReport: function (defaults) {
            const me = this;

            const exportFormats = me.getExportFormats();
            const format = me.getProp('format') || _.head(exportFormats);

            const reportAttrs = _.defaults({}, defaults, {
                instanceId: me.id,
                format: format,
                fileTypes: exportFormats,
                emails: new RivalIQ.collections.Emails({
                    email: RivalIQ.state.user.get('email')
                }),
                title: me.getTitle(format),
                timePeriodType: RivalIQ.enums.timePeriod.period.day
            }, me.getReportAttributes(format));

            return new RivalIQ.export.models.ScheduledReport(reportAttrs, {
                dashboardModel: me
            });
        },

        transformExportProps: function (props, changed) {
            const me = this;
            const formatName = s.titleize(props.format);
            const fn = me[`process${formatName}ExportProps`];
            if (fn) {
                return fn.call(me, props, changed);
            }

            if (me.processExportProps) {
                return me.processExportProps(props, changed);
            }

            return me.processProps(props, changed);
        },

        getFilename: function (format) {
            const me = this;

            if (me.filename) {
                return me.filename;
            }

            let exportFormat = format;
            const exportFormats = me.getExportFormats();
            if (_.isUndefined(format)) {
                exportFormat = (me.getProp('format') || _.head(exportFormats));
            }

            const channel = _.get(RivalIQ.enums.sourceDetails, [me.getProp('source'), 'name']);

            return RivalIQ.export.filename.base({
                title: me.getFilenameTitle(exportFormat),
                landscapeName: RivalIQ.state.view.get('landscapeName'),
                companyId: me.getProp('companyId'),
                channel: channel
            });
        },

        getFilenameTitle: function (format) {
            const me = this;
            return me.getTitle(format);
        },

        isExportActionDisabled: _.noop,

        scheduledExportAdvancedSettingsEnabled: true,

        isExportHD: function () {
            const me = this;
            return (me.getExportWidth() > 800);
        }
    };

    mixins.ExportHD = {
        getExportWidth: _.constant(1200),
        getUsePdfPostMargin: _.constant(true)
    };

}, window.s);
